const getBlogs = async (termData, cacheCleared) => {
  // Create a string key for the cache
  const cacheKey = Object.entries(termData).reduce((prev, curr) => {
    if (!Array.isArray(curr) || curr.length < 2) {
      return prev;
    }
    return `${prev}-${curr?.[0].toString()}-${curr?.[1].toString()}`;
  }, 'blog-cache-key');

  // Check our cache & parse
  const cached = localStorage.getItem(cacheKey);
  let parsedCache;
  try {
    parsedCache = JSON.parse(cached);
  } catch {
    parsedCache = null;
  }

  // Return cached response if we have one that isn't outdated
  const now = new Date();
  if (
    parsedCache &&
    parsedCache?.value &&
    parsedCache?.expiry >= now.getTime() &&
    parsedCache?.cacheCleared === cacheCleared
  ) {
    return parsedCache.value;
  }

  // Make API call
  const response = await fetch(wpArgs.ajaxUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(termData),
  });
  const responseJSON = await response.json();

  // Handle Error
  if (responseJSON?.error || !responseJSON.success || !responseJSON?.data) {
    return { error: true };
  }

  // Make new cache key
  const item = {
    value: responseJSON.data,
    expiry: now.getTime() + 43200, // Time + 12 hrs
    cacheCleared,
  };
  localStorage.setItem(cacheKey, JSON.stringify(item));

  // Return API response
  return responseJSON.data;
};

export const initBlogLoader = () => {
  const indexFilter = document.getElementById('filter-form');
  const inputButtons = document.querySelectorAll('.btn-filter-primary-input');
  const holder = document.querySelector('.blog-index__blog-holder');
  const templateRaw = document.querySelector('.blog-card-template');
  const clearButton = document.querySelector('.btn-filter-clear');
  const loadMore = document.querySelector('.blog-index__load-more__button');
  const termInput = document.getElementById('blog-category-term');
  const cacheTime = document.getElementById('blog-cache-cleared-time');

  // Bail if we don't have required elements
  if (!inputButtons || !holder || !loadMore || !termInput || !templateRaw) {
    return;
  }

  // Doing this because WAVE still sees the template even if it's aria-hidden
  // But if we just remove it right away and store it in memory, we should be fine.
  const template = templateRaw.cloneNode(true);
  templateRaw.remove();

  // Get our index from the query params.
  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });
  let { index = 1 } = params;
  if (!index) {
    index = 1;
  }
  index = parseInt(index, 10);

  // Flag to stop spamming
  let queryRunning = false;

  const term = termInput?.dataset?.value;
  const cacheCleared = cacheTime?.dataset?.value;

  // Actual "Work" function
  const updateBlogs = async () => {
    queryRunning = true;

    const formDataObject = { action: 'get_blogs' };
    if (indexFilter) {
      const formData = new FormData(indexFilter);
      formData.forEach((value, key) => {
        formDataObject[key] = value;
      });
    }
    const termData = {
      index,
      ...formDataObject,
      ...(term ? { 'blog-category': term } : {}),
    };

    // Disable spam
    loadMore.disabled = queryRunning;
    inputButtons.forEach((ib) => {
      ib.disabled = true;
    });

    const data = await getBlogs(termData, cacheCleared);

    // Bail with error
    if (!data || data?.error) {
      queryRunning = false;
      inputButtons.forEach((ib) => {
        ib.disabled = false;
      });
      holder.append('There was an error');
    }

    // Clear out all cards if we're on the first page
    if (index <= 1) {
      holder.innerHTML = '';
    }

    // Handle no items
    if (!data?.blogs || !Array.isArray(data.blogs) || !data.blogs.length) {
      // Update URL
      const windowUrl = new URL(window.location);
      windowUrl.searchParams.set('index', index);
      window.history.pushState({}, '', windowUrl);

      // Undisable buttons / inputs as needed
      loadMore.disabled = data.index >= data.max_num_pages;
      queryRunning = false;
      inputButtons.forEach((ib) => {
        ib.disabled = false;
      });

      const br = document.createElement('br');
      const textNode = document.createTextNode(
        'Sorry, nothing found that meets that criteria.',
      );
      holder.appendChild(br);
      holder.appendChild(textNode);
      return;
    }

    // Make Cards
    const blogCards = data.blogs.map((item) => {
      // Bail without at least title & link
      if (!item?.link || !item?.title) {
        return '';
      }
      const card = template.cloneNode(true);
      // Set anchor link
      card.querySelector('.blog-card__link').href = item.link;

      // Set title
      card.querySelector('.blog-card__title').innerHTML = item.title;

      // Set image
      if (item?.img) {
        const img = card.querySelector('.blog-card__img');
        img.src = item.img?.src;
        img.srcset = item.img?.srcset;
        img.sizes = item.img?.sizes;
        img.alt = item.img?.alt;
        if (item.img?.class) {
          const classes = item.img.class.split(' ');
          classes
            .filter((thisClass) => !!thisClass)
            .forEach((thisClass) => img.classList.add(thisClass));
        }
      }

      // Set Publish Date
      if (item.publish_date) {
        card.querySelector(
          '.blog-card__blog-spec--publish-date span',
        ).innerHTML = item.publish_date;
      } else {
        card.querySelector('.blog-card__blog-spec--publish-date span').remove();
      }

      // Set Description
      if (item.description) {
        card.querySelector('.blog-card__description').innerHTML =
          item.description;
      } else {
        card.querySelector('.blog-card__description').remove();
      }
      return card;
    });

    // Append Cards
    blogCards.forEach((card) => {
      holder.appendChild(card);
    });

    // Update URL
    const windowUrl = new URL(window.location);
    windowUrl.searchParams.set('index', index);
    window.history.pushState({}, '', windowUrl);

    // Undisable buttons / inputs as needed
    loadMore.disabled = data.index >= data.max_num_pages;
    queryRunning = false;
    inputButtons.forEach((ib) => {
      ib.disabled = false;
    });
  };

  // Event handlers - Filter
  inputButtons.forEach((inputButton) => {
    inputButton.addEventListener('change', () => {
      if (queryRunning) {
        return;
      }
      index = 1;
      updateBlogs();
    });
  });
  clearButton?.addEventListener('click', () => {
    if (queryRunning) {
      return;
    }
    index = 1;
    updateBlogs();
  });

  // Event Handlers - Load More
  loadMore.addEventListener('click', () => {
    if (queryRunning) {
      return;
    }
    index += 1;
    updateBlogs();
  });
};
